import { Controller } from "stimulus";
import { dateLabel, esc, monthLabel, request } from "../../support/admin-page";

// The Tract Report scenario lab page (/admin/tract-report-lab). Everything is
// read from Admin::Api::V1::TractReport; expected and actual values, the unlock
// month and the payload come from the API, nothing is recomputed here.
const API = "/admin/api/v1/tract_report";
const METRICS = [
    ["occupancy", "Occupancy"],
    ["achieved_rate", "Achieved rate"],
    ["street_rate", "Street rate"],
    ["move_ins", "Move-ins"],
    ["move_outs", "Move-outs"],
    ["length_of_stay", "Length of stay"],
];

function countsText(counts) {
    if (!counts) return "";
    return Object.keys(counts).map(key => counts[key] + " " + key.replace(/_/g, " ")).join(", ");
}

export default class extends Controller {
    static targets = ["list", "main", "toast"];

    connect() {
        this.state = {
            scenarios: [], orgs: [], defaultAnchor: "", selectedId: null, orgId: null, anchor: "",
            asOf: "", metric: "occupancy", byUnitSize: false, showJson: false, preview: null, previewError: null,
            busy: false,
        };
        this.previewSeq = 0;
        Promise.all([request("GET", API + "/scenarios"), request("GET", API + "/viewer_orgs")])
            .then(([scenarios, orgs]) => {
                this.state.scenarios = scenarios.scenarios;
                this.state.defaultAnchor = scenarios.default_anchor_month;
                this.state.orgs = orgs.viewer_orgs;
                this.select(this.state.scenarios[0] && this.state.scenarios[0].id);
            })
            .catch(error => {
                this.listTarget.innerHTML = '<p class="tract-report-lab__error tract-report-lab__pad">' + esc(error.message) + "</p>";
            });
    }

    // ---- state ----------------------------------------------------------

    get selected() {
        return this.state.scenarios.find(s => s.id === this.state.selectedId);
    }

    select(id) {
        const scenario = this.state.scenarios.find(s => s.id === id);
        if (!scenario) return this.render();
        const holder = scenario.viewer_org && this.state.orgs.find(o => o.id === scenario.viewer_org.id);
        const current = this.state.orgs.find(o => o.id === this.state.orgId);
        Object.assign(this.state, {
            selectedId: id,
            orgId: holder ? holder.id : (current || this.state.orgs[0] || {}).id,
            anchor: scenario.anchor_month || this.state.defaultAnchor,
            asOf: scenario.suggested_as_of[scenario.suggested_as_of.length - 1] || this.state.asOf,
            preview: null,
            previewError: null,
            showJson: false,
        });
        this.render();
        this.loadPreview();
    }

    reloadScenarios() {
        return Promise.all([request("GET", API + "/scenarios"), request("GET", API + "/viewer_orgs")])
            .then(([scenarios, orgs]) => {
                this.state.scenarios = scenarios.scenarios;
                this.state.orgs = orgs.viewer_orgs;
            });
    }

    loadPreview() {
        const scenario = this.selected;
        const seq = ++this.previewSeq;
        if (!scenario || !scenario.applied || !this.state.asOf) return;

        const params = new URLSearchParams({ as_of: this.state.asOf, metric: METRICS.map(m => m[0]).join(",") });
        if (this.state.byUnitSize) params.set("breakdown", "unit_size");
        this.state.previewError = null;
        this.renderResults();
        request("GET", API + "/scenarios/" + encodeURIComponent(scenario.id) + "/preview?" + params.toString())
            .then(preview => {
                if (seq !== this.previewSeq) return;
                this.state.preview = preview;
                this.render();
            })
            .catch(error => {
                if (seq !== this.previewSeq) return;
                this.state.preview = null;
                this.state.previewError = error.message;
                this.render();
            });
    }

    showToast(message, isError) {
        this.toastTarget.hidden = false;
        this.toastTarget.className = "tract-report-lab__toast" + (isError ? " tract-report-lab__toast--error" : "");
        this.toastTarget.innerHTML = '<span class="tract-report-lab__grow">' + esc(message) + "</span>" +
            '<button type="button" aria-label="Dismiss" data-action="admin--tract-report-lab#dismissToast">&times;</button>';
    }

    // ---- actions --------------------------------------------------------

    pick(event) {
        this.select(event.currentTarget.dataset.id);
    }

    setOrg(event) {
        this.state.orgId = Number(event.target.value);
        this.render();
    }

    setAnchor(event) {
        if (event.target.value) this.state.anchor = event.target.value;
    }

    setAsOf(event) {
        const value = event.currentTarget.value || event.currentTarget.dataset.date;
        if (!value) return;
        this.state.asOf = value;
        this.render();
        this.loadPreview();
    }

    setMetric(event) {
        this.state.metric = event.target.value;
        this.renderResults();
    }

    toggleUnitSize(event) {
        this.state.byUnitSize = event.target.checked;
        this.loadPreview();
    }

    toggleJson() {
        this.state.showJson = !this.state.showJson;
        this.renderResults();
    }

    dismissToast() {
        this.toastTarget.hidden = true;
    }

    apply() {
        const scenario = this.selected;
        const org = this.state.orgs.find(o => o.id === this.state.orgId);
        if (!scenario || !org) return;
        this.state.busy = true;
        this.render();
        request("POST", API + "/scenarios/" + encodeURIComponent(scenario.id) + "/application",
                { viewer_org_id: org.id, anchor_month: this.state.anchor })
            .then(result => {
                const replaced = result.replaced && this.state.scenarios.find(s => s.id === result.replaced);
                this.showToast('Applied "' + scenario.title + '" to ' + org.name + ", anchored on " +
                    monthLabel(this.state.anchor) + "." +
                    (replaced ? ' It replaced "' + replaced.title + '", which was removed from that org.' : ""));
                return this.reloadScenarios();
            })
            .catch(error => this.showToast("Apply failed: " + error.message, true))
            .then(() => {
                this.state.busy = false;
                this.select(scenario.id);
            });
    }

    remove() {
        const scenario = this.selected;
        if (!scenario) return;
        this.state.busy = true;
        this.render();
        request("DELETE", API + "/scenarios/" + encodeURIComponent(scenario.id) + "/application")
            .then(result => {
                this.showToast('Removed "' + scenario.title + '": ' + countsText(result.deleted) + " deleted.");
                return this.reloadScenarios();
            })
            .catch(error => this.showToast("Remove failed: " + error.message, true))
            .then(() => {
                this.state.busy = false;
                this.select(scenario.id);
            });
    }

    removeAll() {
        const applied = this.state.scenarios.filter(s => s.applied).length;
        if (!window.confirm("Remove all lab data?\n\nThis deletes every scenario's lab orgs, and the LAB- facilities, " +
            "unit types and rows they added to your orgs. Your orgs' real data is not touched. " +
            applied + " scenario(s) are applied.")) return;
        request("DELETE", API + "/application")
            .then(() => {
                this.showToast("Removed all lab data.");
                return this.reloadScenarios();
            })
            .catch(error => this.showToast("Remove all failed: " + error.message, true))
            .then(() => this.select(this.state.selectedId));
    }

    // ---- rendering ------------------------------------------------------

    render() {
        this.renderList();
        const scenario = this.selected;
        if (!scenario) {
            this.mainTarget.innerHTML = "";
            return;
        }
        this.mainTarget.innerHTML = this.scenarioCard(scenario) + this.timeCard(scenario) +
            '<div data-results="true"></div>';
        this.renderResults();
    }

    renderList() {
        const scenarios = this.state.scenarios;
        const groups = [];
        scenarios.forEach(s => {
            const last = groups[groups.length - 1];
            if (last && last.name === s.group) last.items.push(s);
            else groups.push({ name: s.group, items: [s] });
        });
        const applied = scenarios.filter(s => s.applied).length;
        this.listTarget.innerHTML =
            '<div class="tract-report-lab__card-head"><strong class="tract-report-lab__grow">Scenarios</strong>' +
            '<span class="tract-report-lab__muted">' + applied + " of " + scenarios.length + " applied</span></div>" +
            groups.map(group =>
                '<div class="tract-report-lab__group">' + esc(group.name) + "</div>" +
                group.items.map(s =>
                    '<button type="button" class="tract-report-lab__row' +
                    (s.id === this.state.selectedId ? " tract-report-lab__row--selected" : "") + '"' +
                    ' aria-pressed="' + (s.id === this.state.selectedId) + '" data-id="' + esc(s.id) + '"' +
                    ' data-action="admin--tract-report-lab#pick">' +
                    '<span class="tract-report-lab__number">' + s.number + "</span>" +
                    '<span class="tract-report-lab__grow">' + esc(s.title) + "</span>" +
                    (s.applied ? '<span class="tract-report-lab__badge" title="' + esc(s.viewer_org && s.viewer_org.name) + '">' +
                        esc(s.viewer_org && s.viewer_org.name) + "</span>" : "") +
                    "</button>").join("")).join("");
    }

    scenarioCard(s) {
        const state = this.state;
        const org = state.orgs.find(o => o.id === state.orgId);
        const holds = org && org.applied_scenario && org.applied_scenario !== s.id &&
            state.scenarios.find(x => x.id === org.applied_scenario);
        const orgOptions = state.orgs.map(o =>
            '<option value="' + o.id + '"' + (o.id === state.orgId ? " selected" : "") + ">" + esc(o.name) +
            (o.active ? " (your dashboard org)" : "") +
            (o.has_real_contributions ? " (has real data)" : "") + "</option>").join("");
        const disabled = state.busy ? " disabled" : "";

        return '<section class="tract-report-lab__card tract-report-lab__pad tract-report-lab__stack" aria-label="Selected scenario">' +
            '<div><div class="tract-report-lab__eyebrow">' + esc(s.group) + " · Scenario " + s.number + "</div>" +
            '<h3 class="tract-report-lab__title">' + esc(s.title) + "</h3>" +
            '<p class="tract-report-lab__muted">' + esc(s.description) + "</p></div>" +
            '<div class="tract-report-lab__controls">' +
            '<label class="tract-report-lab__field">Your facility goes in' +
            (state.orgs.length
                ? '<select data-action="change->admin--tract-report-lab#setOrg">' + orgOptions + "</select>"
                : '<span class="tract-report-lab__error">You belong to no organization to apply into.</span>') +
            "</label>" +
            '<label class="tract-report-lab__field">Anchor month' +
            '<input type="month" value="' + esc(state.anchor) + '" data-action="change->admin--tract-report-lab#setAnchor"></label>' +
            '<span class="tract-report-lab__grow"></span>' +
            (s.applied ? '<button type="button" class="tract-report-lab__button"' + disabled +
                ' data-action="admin--tract-report-lab#remove">Remove data</button>' : "") +
            '<button type="button" class="tract-report-lab__button tract-report-lab__button--primary"' +
            (state.busy || !org ? " disabled" : "") + ' data-action="admin--tract-report-lab#apply">' +
            (state.busy ? "Working…" : s.applied ? "Reapply" : "Apply") + "</button></div>" +
            (holds ? '<div class="tract-report-lab__notice">One scenario per org: ' + esc(org.name) + ' holds "' +
                esc(holds.title) + '". Applying removes it first, because give to get counts every facility in the org.</div>' : "") +
            (org && org.has_real_contributions ? '<div class="tract-report-lab__warning">' + esc(org.name) +
                " has real contributed data. Its months count toward give to get, so some checks may not match.</div>" : "") +
            (s.applied
                ? '<div class="tract-report-lab__ok">Applied to ' + esc(s.viewer_org && s.viewer_org.name) + ", anchored on " +
                  esc(monthLabel(s.anchor_month)) + ": " + esc(countsText(s.counts)) + "</div>"
                : '<div class="tract-report-lab__muted">Not applied. Nothing from this scenario is in the database.</div>') +
            "</section>";
    }

    timeCard(s) {
        const state = this.state;
        const chips = s.suggested_as_of.map(date =>
            '<button type="button" class="tract-report-lab__chip' + (date === state.asOf ? " tract-report-lab__chip--active" : "") + '"' +
            ' aria-pressed="' + (date === state.asOf) + '" data-date="' + esc(date) + '"' +
            ' data-action="admin--tract-report-lab#setAsOf">' + esc(dateLabel(date)) + "</button>").join("");
        const preview = state.preview;
        const unlocked = preview && preview.as_of === state.asOf ? preview.unlocked_through : null;
        // Relative: the dashboard app is served from the same host as /admin.
        // The report lives in the dashboard app, on its own host.
        const base = (this.element.dataset.dashboardUrl || "").replace(/\/$/, "");
        const href = base + "/portfolio/operating-data?as_of=" + encodeURIComponent(state.asOf);

        return '<section class="tract-report-lab__card tract-report-lab__pad tract-report-lab__stack" aria-label="Time travel">' +
            '<div class="tract-report-lab__controls">' +
            '<label class="tract-report-lab__field">Pretend today is' +
            '<input type="date" value="' + esc(state.asOf) + '" data-action="change->admin--tract-report-lab#setAsOf"></label>' +
            '<div class="tract-report-lab__field"><span class="tract-report-lab__muted">This scenario\'s dates</span>' +
            '<div class="tract-report-lab__chips">' + chips + "</div></div>" +
            '<span class="tract-report-lab__grow"></span>' +
            (s.applied ? '<a class="tract-report-lab__link" href="' + esc(href) + '" target="_blank" rel="noopener">Open in your account on ' +
                esc(dateLabel(state.asOf)) + '<i class="material-icons">open_in_new</i></a>' : "") +
            "</div>" +
            (unlocked ? '<p class="tract-report-lab__muted tract-report-lab__small">Any date works. On ' + esc(dateLabel(state.asOf)) +
                " the newest unlocked month is " + esc(monthLabel(unlocked)) +
                " (months unlock on the 3rd of the month after next).</p>"
                : '<p class="tract-report-lab__muted tract-report-lab__small">Any date works. Months unlock on the 3rd of the month after next.</p>') +
            "</section>";
    }

    renderResults() {
        const holder = this.mainTarget.querySelector("[data-results]");
        const scenario = this.selected;
        if (!holder || !scenario) return;
        if (!scenario.applied) {
            holder.innerHTML = '<section class="tract-report-lab__placeholder">Apply the scenario to run its checks and preview the report.</section>';
            return;
        }
        if (this.state.previewError) {
            holder.innerHTML = '<section class="tract-report-lab__card tract-report-lab__pad tract-report-lab__error">' +
                esc(this.state.previewError) + "</section>";
            return;
        }
        const preview = this.state.preview;
        if (!preview || preview.as_of !== this.state.asOf) {
            holder.innerHTML = '<section class="tract-report-lab__card tract-report-lab__pad tract-report-lab__muted">Running checks…</section>';
            return;
        }
        holder.innerHTML = this.checksCard(preview) + this.reportCard(preview);
    }

    checksCard(preview) {
        const checks = preview.checks;
        const failures = checks.filter(c => !c.pass).length;
        const summary = failures ? failures + " of " + checks.length + " fail" : checks.length + " of " + checks.length + " pass";
        return '<section class="tract-report-lab__card" aria-label="Checks">' +
            '<div class="tract-report-lab__card-head"><h3 class="tract-report-lab__grow">Checks</h3>' +
            '<span class="tract-report-lab__pill' + (failures ? " tract-report-lab__pill--fail" : "") + '">' + summary + "</span></div>" +
            '<div class="tract-report-lab__scroll"><table class="tract-report-lab__table"><thead><tr>' +
            "<th>Pretend today</th><th>Check</th><th>Expected</th><th>Actual</th><th>Result</th></tr></thead><tbody>" +
            checks.map(c =>
                "<tr" + (c.as_of === this.state.asOf ? ' class="tract-report-lab__current"' : "") + ">" +
                '<td><button type="button" class="tract-report-lab__date" data-date="' + esc(c.as_of) + '"' +
                ' data-action="admin--tract-report-lab#setAsOf">' + esc(dateLabel(c.as_of)) + "</button></td>" +
                "<td>" + esc(c.label) + "</td><td>" + esc(c.expected) + "</td><td>" + esc(c.actual) + "</td>" +
                '<td><span class="tract-report-lab__pill' + (c.pass ? "" : " tract-report-lab__pill--fail") + '">' +
                (c.pass ? "Pass" : "Fail") + "</span></td></tr>").join("") +
            "</tbody></table></div></section>";
    }

    reportCard(preview) {
        const payload = preview.payload;
        const market = payload.market;
        const metric = this.state.metric;
        const metricLabel = METRICS.find(m => m[0] === metric)[1];
        const status = market.status === "ok"
            ? "ok · " + market.facility_count + " facilities, " + market.operator_count + " operators · data " +
              market.data_from + " to " + market.data_through
            : "suppressed (" + market.reason + "). No series are returned for a suppressed market.";

        let body;
        if (this.state.showJson) {
            body = '<pre class="tract-report-lab__json">' + esc(JSON.stringify(preview, null, 2)) + "</pre>";
        } else if (market.status !== "ok") {
            body = "";
        } else {
            const periods = payload.meta.periods;
            const series = payload.series.filter(s => s.metric === metric);
            let suppressed = false;
            let notContributed = false;
            const rows = series.map(s => {
                const parts = [s.group === "comp" ? "Comp set" : "Your stores"];
                Object.keys(s.breakdown).forEach(k => { if (s.breakdown[k]) parts.push(s.breakdown[k]); });
                const byPeriod = {};
                s.points.forEach(p => { byPeriod[p.period] = p; });
                return "<tr><th scope=\"row\">" + esc(parts.join(" · ")) + "</th>" + periods.map(period => {
                    const p = byPeriod[period];
                    if (!p) return "<td>–</td>";
                    if (p.contributed === false) { notContributed = true; return '<td class="tract-report-lab__gap">Not contributed</td>'; }
                    if (p.suppressed) { suppressed = true; return '<td class="tract-report-lab__gap">Suppressed</td>'; }
                    return "<td>" + (p.value == null ? "–" : esc(p.value)) + "</td>";
                }).join("") + "</tr>";
            }).join("");
            // Same wording as the dashboard's Tract Report (helpers/insight-panel/tractReport/messages.ts).
            const notes = [];
            if (suppressed) {
                notes.push("Gaps are months where fewer than " + payload.meta.min_facilities + " facilities from " +
                    payload.meta.min_operators + " operators reported " + metricLabel.toLowerCase() +
                    ", or one facility held 70% or more of the units.");
            }
            if (notContributed) {
                notes.push(suppressed ? "Months you didn't contribute data are left out too." : "Gaps are months you didn't contribute data.");
            }
            body = '<div class="tract-report-lab__scroll"><table class="tract-report-lab__table"><thead><tr><th>Series</th>' +
                periods.map(p => "<th>" + esc(p) + "</th>").join("") + "</tr></thead><tbody>" +
                (rows || '<tr><td colspan="' + (periods.length + 1) + '" class="tract-report-lab__muted">No series for ' +
                    esc(metricLabel.toLowerCase()) + ".</td></tr>") +
                "</tbody></table></div>" +
                (notes.length ? '<p class="tract-report-lab__note">' + esc(notes.join(" ")) + "</p>" : "");
        }

        return '<section class="tract-report-lab__card" aria-label="Report preview">' +
            '<div class="tract-report-lab__card-head"><h3 class="tract-report-lab__grow">Report on ' + esc(dateLabel(preview.as_of)) + "</h3>" +
            '<label class="tract-report-lab__inline"><input type="checkbox"' + (this.state.byUnitSize ? " checked" : "") +
            ' data-action="change->admin--tract-report-lab#toggleUnitSize"> By unit size</label>' +
            '<label class="tract-report-lab__inline">Metric <select data-action="change->admin--tract-report-lab#setMetric">' +
            METRICS.map(m => '<option value="' + m[0] + '"' + (m[0] === metric ? " selected" : "") + ">" + m[1] + "</option>").join("") +
            "</select></label>" +
            '<button type="button" class="tract-report-lab__text-button" data-action="admin--tract-report-lab#toggleJson">' +
            (this.state.showJson ? "View table" : "View JSON") + "</button></div>" +
            '<div class="tract-report-lab__market">Market: <strong>' + esc(status) + "</strong></div>" +
            (preview.warning ? '<div class="tract-report-lab__warning tract-report-lab__inset">' + esc(preview.warning) + "</div>" : "") +
            body + "</section>";
    }
}
